<template>

	<div ref="selectors-row" class="row selectors-row" :class="{collapsed: collapsedSelectors, showing: surpassedFilters && !collapsedSelectors}">
		<div class="period-picker">
			<AppSwitch
				fixed-color
				left-icon="iconClock"
				right-icon="iconCalendar"
				:checked="rangeMode"
				@switched="switchMode"
				/>
			<AppSelect
				v-if="rangeMode"
				:values="timeNames"
				:selected-index="selectedTimeIndex"
				@selected="changedTimeframe"
			/>
			<DatePicker ref="date-picker-range" v-model.range="selectedRange" :mode="rangeMode ? 'date' : 'datetime'" @update:modelValue="changedCalendarDates" color="teal" :is-dark="userStore.darkMode" is24hr>
				<template #default="{ togglePopover }">
					<div ref="period-phrases" class="period-phrases" @click="togglePopover">
						<p class="period">{{periodPhrase}}</p>
						<p class="old-period">{{oldPeriodPhrase}}</p>
					</div>
				</template>
			</DatePicker>
		</div>

		<div class="slots-container">
			<AppLoader :size="20" class="period-loader" :style="{ visibility: loading ? 'visible' : 'hidden' }" />
			<slot name="extra-filters"></slot>
			<AppSwitch
				v-if="withTableSwitch"
				fixed-color
				left-icon="iconChart"
				right-icon="iconTable"
				@switched="(checked) => $emit('switched-chart', checked)"
			/>
		</div>
	</div>

</template>

<script>
import {useLanguageStore} from "@/shared/stores";
import AppSelect from "@/shared/components/AppSelect.vue";
import AppSwitch from "@/shared/components/AppSwitch.vue";
import AppLoader from "@/shared/components/AppLoader.vue";
import {useAnalyticsStore, useUserStore} from "@/console-new/stores";
import {DatePicker} from "v-calendar";
import AppTextInput from "@/shared/components/AppTextInput.vue";

export default {
	setup() {
		const languageStore = useLanguageStore();
		const analyticsStore = useAnalyticsStore();
		const userStore = useUserStore();
		return {analyticsStore, getString: languageStore.getString, languageStore, userStore};
	},
	name: "FiltersRow",
	components: {AppTextInput, AppSwitch, AppLoader, DatePicker, AppSelect },
	inject: ["providedAction"],
	emits: ['changed-timeframe', "switched-chart"],
	data() {
		return {
			collapsedSelectors: false,
			scrollLen: 0,
			selectedTimeIndex: 0,
			rangeMode: true,
			timeMode: 0,
			timeNames: [
				this.getString('1_week'),
				this.getString('4_weeks'),
				this.getString('this_month'),
				this.getString('3_months'),
				this.getString('6_months'),
				this.getString('1_year'),
				this.getString('this_year'),
				this.getString('custom'),
			],
			selectedRange: {
				start: new Date(),
				end: new Date()
			},
			switchingView: false,
		};
	},
	props: {
		withTableSwitch: {
			type: Boolean,
			default: false
		},
		loading: {
			type: Boolean,
			default: false
		},
	},
	computed: {
		surpassedFilters() {
			let initialCard = this.$parent.$refs['initial-card'] ? this.$parent.$refs['initial-card'].$el : null;
			let targetScroll = initialCard && initialCard.offsetHeight ? initialCard.offsetHeight : 0;
			targetScroll += 100;
			return this.scrollLen >= targetScroll;
		},
		periodPhrase() {
			let endDateDB = new Date(this.targetEndDate);
			let startDateDB = this.targetStartDate;
			let fullYear = endDateDB.getFullYear() !== startDateDB.getFullYear() || endDateDB.getFullYear() !== new Date().getFullYear() || [5,6].includes(this.selectedTimeIndex);
			let shortTime = this.selectedTimeIndex === 7 && (endDateDB.getTime() - startDateDB.getTime()) <= 2*86400000;
			if (this.selectedTimeIndex === 7 && this.analyticsStore.getDayDifference(startDateDB, endDateDB) > 2) {
				endDateDB.setDate(endDateDB.getDate() - 1);
			}
			return this.localeDateString(startDateDB, fullYear, shortTime) + ' - ' + (this.selectedTimeIndex === 7 ? this.localeDateString(endDateDB, fullYear, shortTime) : this.getString('today'));
		},
		oldPeriodPhrase() {
			let endDateDB = new Date(this.targetPreviousEndDate);
			let startDateDB = this.targetPreviousStartDate;
			let fullYear = endDateDB.getFullYear() !== startDateDB.getFullYear() || endDateDB.getFullYear() !== new Date().getFullYear() || [5,6].includes(this.selectedTimeIndex);
			let shortTime = this.selectedTimeIndex === 7 && (endDateDB.getTime() - startDateDB.getTime()) < 2*86400000;
			if(this.analyticsStore.getDayDifference(startDateDB, endDateDB) > 2) {
				endDateDB.setDate(endDateDB.getDate() - 1);
			}
			return this.localeDateString(startDateDB, fullYear, shortTime) + ' - ' + this.localeDateString(endDateDB, fullYear, shortTime);
		},
		targetStartDate() {
			if(this.selectedTimeIndex === 7) {
				let start = new Date(this.selectedRange.start);
				if(this.rangeMode) {
					let timeShift = this.userStore.currBusiness && this.userStore.currBusiness.dayShift ? this.userStore.currBusiness.dayShift : "03:00:00";
					let timeShiftArr = timeShift.split(":");
					start.setHours(Number(timeShiftArr[0]) || 3, Number(timeShiftArr[1]) || 0, Number(timeShiftArr[2]) || 0, 0);
				}
				return start;
			} else {
				return this.analyticsStore.getTargetStartDate(this.selectedTimeIndex);
			}
		},
		targetEndDate() {
			if(this.selectedTimeIndex === 7) {
				let end = new Date(this.selectedRange.end);
				if(this.rangeMode) {
					let timeShift = this.userStore.currBusiness && this.userStore.currBusiness.dayShift ? this.userStore.currBusiness.dayShift : "03:00:00";
					let timeShiftArr = timeShift.split(":");
					end.setHours(Number(timeShiftArr[0]) || 3, Number(timeShiftArr[1]) || 0, Number(timeShiftArr[2]) || 0, 0);
				}
				return end;
			} else {
				return this.analyticsStore.getTargetEndDate();
			}
		},
		targetPreviousStartDate() {
			if(this.selectedTimeIndex === 7) {
				let dateDiff = this.targetEndDate.getTime() - this.targetStartDate.getTime();
				return new Date(this.targetStartDate.getTime() - dateDiff);
			} else {
				return this.analyticsStore.getTargetPreviousStartDate(this.selectedTimeIndex);
			}
		},
		targetPreviousEndDate() {
			if(this.selectedTimeIndex === 7) {
				let startDate = new Date(this.targetStartDate);
				startDate.setMinutes(startDate.getMinutes() - 1);
				return startDate;
			} else {
				return this.analyticsStore.getTargetPreviousEndDate(this.selectedTimeIndex);
			}
		},
	},
	methods: {
		switchMode(checked) {
			this.rangeMode = checked;
			this.switchingView = true;
			if(!checked) {
				let startDate = new Date();
				let timeShift = this.userStore.currBusiness && this.userStore.currBusiness.dayShift ? this.userStore.currBusiness.dayShift : "03:00:00";
				let timeShiftArr = timeShift.split(":");
				startDate.setHours(Number(timeShiftArr[0]) || 3, Number(timeShiftArr[1]) || 0, Number(timeShiftArr[2]) || 0, 0);
				let endDate = new Date(startDate);
				endDate.setDate(endDate.getDate()+1);
				this.selectedRange = {start: startDate, end: endDate};
			} else {
				this.changedTimeframe(0);
			}
		},
		changedCalendarDates(selectedRange) {
			if(!this.switchingView && this.rangeMode){
				this.selectedRange.end.setDate(selectedRange.end.getDate() + 1);
			}
			this.switchingView = false;
			this.selectedTimeIndex = 7;
			this.$emit('changed-timeframe', this.targetStartDate, this.targetEndDate, this.targetPreviousStartDate, this.targetPreviousEndDate, this.selectedTimeIndex);
		},
		changedTimeframe(newIndex) {
			let currentStartDate = new Date(this.targetStartDate);
			let currentEndDate = new Date(this.targetEndDate);
			this.selectedTimeIndex = newIndex;
			if(newIndex === 7) {
				this.selectedRange = {start: currentStartDate, end: currentEndDate};
				this.$refs['date-picker-range'].showPopover({showDelay: 550});
			} else {
				this.rangeMode = true;
				this.$emit('changed-timeframe', this.targetStartDate, this.targetEndDate, this.targetPreviousStartDate, this.targetPreviousEndDate, this.selectedTimeIndex);
			}
		},
		localeDateString(date, fullYear = false, shortTime = false) {
			let options = {month: 'long', day: 'numeric'};
			if(fullYear) {
				options.year = 'numeric';
				options.month = 'short';
			}
			if(shortTime) {
				options.hour = 'numeric';
				options.minute = 'numeric';
				options.month = 'short';
			}
			return date.toLocaleDateString([this.languageStore.lang || 'en-US'], options);
		},
		scrollListener() {
			let scrollItem = document.querySelector('.main-content');
			this.scrollLen = scrollItem && scrollItem.scrollTop ? scrollItem.scrollTop : 0;
		}
	},
	mounted() {

		this.scrollListener();
		let mainContent = document.querySelector('.main-content');
		if(mainContent) {
			mainContent.addEventListener('scroll', this.scrollListener);
		}

		this.collapsedSelectors = this.providedAction && this.providedAction.collapsedSelectors ? this.providedAction.collapsedSelectors : false;
		this.providedAction.toggleIconFilters(this.surpassedFilters);
	},
	beforeUnmount() {
		let mainContent = document.querySelector('.main-content');
		if(mainContent && this.scrollListener) {
			mainContent.removeEventListener('scroll', this.scrollListener);
		}
	},
	watch: {
		providedAction(newProvidedObject) {
			if(newProvidedObject.hasOwnProperty('collapsedSelectors') && newProvidedObject.collapsedSelectors !== this.collapsedSelectors) {
				this.collapsedSelectors = newProvidedObject.collapsedSelectors;
			}
		},
		surpassedFilters(newState) {
			if(this.providedAction && this.providedAction.toggleIconFilters) {
				this.providedAction.toggleIconFilters(!!newState);
			}
		},
	}
}
</script>

<style lang="scss" scoped>

.selectors-row {
	display: flex;
	flex-direction: row;
	align-items: flex-end;
	margin: 30px 0 20px calc(-1 * var(--main-padding));
	backdrop-filter: blur(0px);
	position: sticky;
	box-shadow: 0 1px 10px rgb(7 82 163 / 0%);
	top: -45px;
	background: var(--color-bg-primary-1);
	z-index: 13;
	width: calc(100% + (var(--main-padding)*2));
	padding: 10px var(--main-padding);
	transition: top .3s ease, box-shadow .3s ease, background .3s ease;
	justify-content: space-between;
	flex-wrap: nowrap;
	gap: 8px;
	min-height: 60px;

	&.collapsed {
		top: -125px;
	}

	.slots-container {
		@include centered;
		@include columnFlex;
		@include noSelect;
		flex-direction: row;
		align-items: center;
		gap: 15px;
		flex-shrink: 0;
		:deep(.icon-button) {
			margin: 0;
		}
	}

	.period-picker {
		display: flex;
		flex-wrap: wrap;
		align-items: flex-end;
		gap: 15px;
		flex-grow: 1;
		.period-loader {
		}
		.period-phrases {
			display: flex;
			flex-direction: column;
			align-self: stretch;
			justify-content: flex-end;
			cursor: pointer;

			p.period, p.old-period {
				opacity: .8;
				white-space: nowrap;
				font-size: 12px;
				line-height: 14px;
			}
			p.old-period {
				color: #c0c0c0;
			}
		}
	}

	.select-wrapper {
		max-width: 190px;
	}

	&.showing {
		box-shadow: 0 1px 10px var(--color-box-shadow-3);
		backdrop-filter: blur(4px);
		-webkit-backdrop-filter: blur(4px);
		background: rgba(var(--rgb-bg-primary-1), 67%);
	}

	.businesses-checklist {
		:deep(small) {
			color: var(--color-txt-2);
			font-size: 10px;
		}
	}

	:deep(.switch-container) {
		width: 92px;
		height: 35px;
		.switch-slider:before, .switch-slider:after {
			height: 29px;
			width: 43px;
			left: 4px;
		}
		input:checked + .switch-slider:before, input:checked + .switch-slider:after {
			@media (max-width: 768px) {
				left: calc(100% - 47px);
			}
		}
	}
}

@media (min-width: 1550px) {
	.selectors-row {
		border-bottom-left-radius: 13px;
		border-bottom-right-radius: 13px;
	}
}
@media (max-width: 1440px) {
	.selectors-row {
		top: -35px;
		&.collapsed {
			top: -115px;
		}
	}
}
@media (max-width: 1080px) {
	.selectors-row {
		top: -25px;
		&.collapsed {
			top: -105px;
		}
	}
}
@media (max-width: 480px) {
	.selectors-row {
		top: -20px;

		&.collapsed {
			top: -175px;
		}
	}
}
@media (max-width: 420px) {
	.selectors-row {
		top: -15px;
		&.collapsed {
			top: -210px;
		}
	}
}
@media (max-width: 380px) {
	.selectors-row {
		>.select-wrapper {
			width: calc(100% - 80px);
		}
	}
}
</style>
